/**
 * @voltxampere/spice — Voltage source codec (V — DC mode)
 *
 * Berkeley SPICE3 syntax for the DC variant:
 *   V{name} N+ N- value
 *   V{name} N+ N- DC value
 *
 * Examples:
 *   V1 vcc 0 5
 *   Vsupply rail 0 DC 12
 *
 * AC and pulse variants are claimed by `acSourceCodec` /
 * `pulseSourceCodec` first (registry order). This codec rejects
 * lines that contain `PULSE(...)`, `SIN(...)`, `EXP(...)`, or the
 * `AC` keyword so dispatch falls through to the right codec.
 *
 * Schema mapping: `type: "dc-source"` (carry-over from canvas-demo
 * symbol-id pattern), params: `{ voltage }`. The schema-canonical
 * type `"voltage-source"` is also owned for forward compatibility
 * with code that follows the components-package canonical names.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam, functionName } from "./_param-helpers.js";
export const voltageSourceCodec = {
    spiceLetter: "V",
    schemaTypes: ["dc-source", "voltage-source"],
    examples: ["V1 vcc 0 5", "Vsupply rail 0 DC 12"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("V needs 2 nodes and a value");
        }
        if (containsAcOrTransientShape(card.tokens)) {
            return err("V is an AC / PULSE / SIN form — defer to specific codec");
        }
        const [na, nb, ...rest] = card.tokens;
        if (na === undefined || nb === undefined) {
            return err("V missing nodes");
        }
        let valueRaw;
        if (rest[0]?.toUpperCase() === "DC")
            valueRaw = rest[1];
        else
            valueRaw = rest[0];
        if (valueRaw === undefined)
            return err("V missing value");
        const value = parseSpiceNumber(valueRaw);
        if (value === null)
            return err(`V value '${valueRaw}' is not a valid number`);
        const component = {
            id: card.identifier,
            type: "dc-source",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params: { voltage: value },
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const v = numericParam(component.params.voltage, 5);
        const [a, b] = terminals;
        return `${component.id} ${a ?? "0"} ${b ?? "0"} ${formatSpiceValue(v)}`;
    },
    owns(schemaType) {
        return schemaType === "dc-source" || schemaType === "voltage-source";
    },
};
function containsAcOrTransientShape(tokens) {
    for (const token of tokens) {
        const fn = functionName(token);
        if (fn === "PULSE" || fn === "SIN" || fn === "EXP" || fn === "PWL") {
            return true;
        }
        if (token.toUpperCase() === "AC")
            return true;
    }
    return false;
}
//# sourceMappingURL=voltage-source.js.map